import { Integration, LocalEditableIntegration } from "../../../Everou/_modelsTS/Integration";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIntegrationName } from "./useIntegrationName";
import CreatePublicUrlToggle from "../../../Everou/InvitationsModule/CreatePublicUrlToggle";
import { CapsuleButton, CustomNumberInput } from "../../../Everou/_components";
import { useDispatch } from "react-redux";
import { MIN_TIME } from "../../../Everou/_constants";
import { useError } from "../../../Everou/_hooks/useError";
import { Icons } from "../../../Everou/_assets";
import SendInvitationURLToggle from "../../../Everou/IntegrationsModule/components/SendInvitationURLToggle";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { CheckInOutFields } from "../../../Everou/IntegrationsModule/components/CheckInOutFields";

type IntegrationInputProps = {
    integration: LocalEditableIntegration;
    onChangeInputs?: (...args: any) => void | any;
    onToggleEditable: (...args: any) => void | any;
    onSetInitialStatus: (...args: any) => void | any;
    onDelete: (index: number) => void;
    index: number;
}

export function PMSGuestyIntegrationInput({ 
    integration, 
    onDelete, 
    index, 
    onToggleEditable }: IntegrationInputProps) {

    // LIBRARIES
    const { t } = useTranslation();
    const { templateName, nameInput } = useIntegrationName(integration);
    const dispatch = useDispatch();
    
    // GLOBAL STATE
    const [usernameValue, setUsernameValue] = useState(integration.username ?? '');
    const [passwordValue, setPasswordValue] = useState(integration.password ?? '');
    const [createPublicUrl, setCreatePublicUrl] = useState(integration.createPublicUrl);
    const [sendInvitationURL, setSendInvitationURL] = useState(integration.sendInvitationURL);
    const { errorMessage, setError, hasError, disableError } = useError();

    const [checkIn, setCheckIn] = useState(integration.check_in ?? MIN_TIME);
    const [checkOut, setCheckOut] = useState(integration.check_out ?? MIN_TIME);


    // INITIALIZATION
    useEffect(() => { 
        setUsernameValue(integration.username ?? '');
        setPasswordValue(integration.password ?? '');
        setCreatePublicUrl(integration.createPublicUrl);
        setSendInvitationURL(integration.sendInvitationURL);
        setCheckIn(integration.check_in ?? '00:00');
        setCheckOut(integration.check_out ?? '00:00');
    }, [integration]);

    const canSave = useMemo(() => {
        if(hasError) return false;

        return !!usernameValue && !!passwordValue &&
        (
            !integration.uid ||
            sendInvitationURL !== integration.sendInvitationURL || 
            createPublicUrl !== integration.createPublicUrl ||
            usernameValue !== integration.username ||
            passwordValue !== integration.password ||
            checkIn !== integration.check_in ||
            checkOut !== integration.check_out
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        integration.uid,
        sendInvitationURL, 
        createPublicUrl, 
        usernameValue, 
        passwordValue,
        hasError,
        checkIn,
        checkOut
    ]);

    const enableSendInvitationURLToggle = useMemo(() => {
        return createPublicUrl;
    }, [createPublicUrl]);
    
    const onSave = useCallback(async () => {
        const editedIntegration: Integration = {
            name: templateName,
            username: usernameValue,
            password: passwordValue,
            company: integration.company,
            uid: integration.uid,
            check_in: checkIn,
            check_out: checkOut,
            createPublicUrl,
            sendInvitationURL,
        }

        try {
            await dispatch(PMSIntegrationsActions.createIntegration(editedIntegration));
        } catch (err: any) {
            setError(err.message)
        }
    }, [
        dispatch,
        templateName, 
        t, 
        setError, 
        integration.company,
        integration.uid, 
        usernameValue,
        passwordValue,
        createPublicUrl,
        checkIn,
        checkOut, 
        sendInvitationURL
    ]);

    return (
        <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
            {nameInput}
            <div className="col-span-full">
                <label className="font-weight-bold mb-1 d-block">Client ID</label>
                <input
                    className={`${hasError ? 'input-error' : ''} ${!integration.editable ? 'cna' : ''} w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    readOnly={!integration.editable}
                    value={usernameValue}
                    placeholder={"Client ID"}
                    onChange={(e) => {
                        setUsernameValue(e.target.value?.trim());
                    }}
                />
            </div>
            <div className="col-span-full">
                <label className="font-weight-bold mb-1 d-block">Client Secret</label>
                <input
                    className={`${hasError ? 'input-error' : ''} ${!integration.editable ? 'cna' : ''} w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    readOnly={!integration.editable}
                    value={passwordValue}
                    placeholder={"Client Secret"}
                    onChange={(e) => {
                        setPasswordValue(e.target.value?.trim());
                    }}
                />
            </div>
            <div className="mt-2">
                <CreatePublicUrlToggle
                    boldedName={false}
                    initialState={createPublicUrl}
                    readOnly={!integration.editable}
                    onChange={setCreatePublicUrl}
                />
                { enableSendInvitationURLToggle && 
                    <div className="mt-2">
                        <SendInvitationURLToggle
                            boldedName={false}
                            initialState={sendInvitationURL}
                            readOnly={!integration.editable}
                            onChange={setSendInvitationURL}
                        />
                    </div>
                }
            </div>
            <CheckInOutFields
                checkIn={checkIn}
                checkOut={checkOut}
                onChangeCheckIn={setCheckIn}
                onChangeCheckOut={setCheckOut}
                readOnly={!integration.editable}
            />
            <div className="col-span-full mt-4 d-flex justify-content-between align-items-center">
                <div className="d-flex">
                    {integration.uid &&
                        <CapsuleButton
                            size="SMALL"
                            classNameExtra="mr-2"
                            style={!integration.editable ? 'PRIMARY' : 'INFO'}
                            onClick={() => onToggleEditable(index)}
                            text={t(!integration.editable ?  "global_edit" : 'global_cancel')}
                        />
                    }
                    {integration.uid &&
                        <CapsuleButton
                            onClick={() => onDelete(index)}
                            classNameExtra="mr-2"
                            // eslint-disable-next-line react/style-prop-object
                            style="DANGER"
                            size="SMALL"
                            icon={Icons.trash}
                        />
                    }
                </div>
                <CapsuleButton
                    size="SMALL"
                    isEnabled={canSave}
                    onClick={onSave}
                    text={t("global_save")}
                />
            </div>
            {errorMessage && (
                <div className="text-danger mr-2">{errorMessage}</div>
            )}
        </div>
    );
}